import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { read } from "hooks/useSession.ts";
import { ValidateProductsResponseData } from "types/api/products/postValidate";
import { models } from "types/api/viewModels.ts";
import { GetAccountsResponseData } from "types/api/accounts/getAccounts.ts";
import { convertShipToToAddress } from "utils/address";

export interface LinkNavigation {
	navItem?: models["UserNavigationItemViewModel"];
	index?: number;
	state: "proceed" | "pause";
}

interface DraftDetails {
	draftOrder: models["DraftOrderViewModel"] | undefined;
	draftError: any;
	// added id state to save drafts and templates in the same session
	currentDraftId?: string;
	currentTemplateId?: string;
}

interface Catalogs {
	catalogsWithCustomFinish: models["CatalogViewModel"][] | undefined;
	catalogsWithoutCustomFinish: models["CatalogViewModel"][] | undefined;
}

const newOrder = read("_newOrder");

export interface NewOrderSlice {
	leaveNewOrderFlow: LinkNavigation | undefined;
	newAddresses: models["ShipToViewModel"][];
	parsedCSV: models["ValidatedOrderViewModel"][] | undefined;
	parsedTempCSV: models["ValidatedOrderViewModel"][] | undefined;
	validatedOrderResponse: ValidateProductsResponseData | undefined;
	newGlobalAttributes: models["PendingOrderConfigurationViewModel"] | undefined;
	updatedGlobalAttributes: models["PendingOrderConfigurationViewModel"] | undefined;
	productLineAccounts: Record<string, GetAccountsResponseData>;
	newOrderDetails: models["PendingOrderViewModel"] | undefined;
	draftDetails: DraftDetails | undefined;
	catalogs: Catalogs | undefined;
}

export const initialState: NewOrderSlice = newOrder || {
	leaveNewOrderFlow: undefined,
	newAddresses: [],
	parsedCSV: undefined,
	parsedTempCSV: undefined,
	validatedOrderResponse: undefined,
	newGlobalAttribute: undefined,
	updatedGlobalAttributes: undefined,
	productLineAccounts: {},
	draftDetails: undefined,
	newOrderDetails: undefined,
	catalogs: undefined,
	catalogsWithCustomFinish: []
};

const newOrderSlice = createSlice({
	name: "newOrder",
	initialState,
	reducers: {
		addNewAddress: (state, action) => {
			const addressMatchCallback = (shipTo: models["ShipToViewModel"] | null) => {
				return (
					shipTo?.address?.line1 === action.payload?.line1 &&
					shipTo?.address?.line2 === action.payload?.line2 &&
					shipTo?.address?.line3 === action.payload?.line3 &&
					shipTo?.address?.city === action.payload?.city &&
					shipTo?.address?.zip === action.payload?.zip
				);
			};

			if (action.payload && !state.newAddresses?.some(addressMatchCallback)) {
				state.newAddresses?.push(action.payload);
			}

			return state;
		},
		updateOrderType: (state, action) => {
			state.newOrderDetails = {
				...state.newOrderDetails,
				orderType: action.payload
			};
			state.draftDetails = {
				draftError: state.draftDetails?.draftError,
				draftOrder: {
					...state.draftDetails?.draftOrder,
					orderType: action.payload
				}
			};
		},
		setDraftId: (
			state,
			action: PayloadAction<{
				currentDraftId: string | undefined;
				isTemplate?: boolean;
			}>
		) => {
			state.draftDetails = {
				draftError: state.draftDetails?.draftError,
				draftOrder: state.draftDetails?.draftOrder,
				currentDraftId: action.payload.isTemplate
					? state.draftDetails?.currentDraftId
					: action.payload.currentDraftId,
				currentTemplateId: action.payload.isTemplate
					? action.payload.currentDraftId
					: state.draftDetails?.currentTemplateId
			};
		},
		addParsedCSV: (state, action: PayloadAction<models["ValidatedOrderViewModel"]>) => {
			state.parsedCSV = [...(state.parsedCSV ?? []), action.payload];
		},
		overwriteParsedCSV: (state, action: PayloadAction<models["ValidatedOrderViewModel"][] | undefined>) => {
			state.parsedCSV = action.payload;
		},
		addTempParsedCSV: (
			state,
			action: PayloadAction<{
				importedCSV: models["ValidatedOrderViewModel"];
				existingConfigs: models["PendingOrderConfigurationViewModel"][];
			}>
		) => {
			const importedCSVData = { ...action.payload.importedCSV };

			const recreatedCSVData: models["ValidatedOrderViewModel"][] = Array.from(
				action.payload.existingConfigs
					?.reduce((accum: Map<number, models["ValidatedOrderViewModel"]>, current) => {
						const findCSVCb = (csv: models["ValidatedOrderViewModel"]) =>
							csv?.configurations?.find((config) => config.configurationId === current.configurationId);
						const existingStateCSVIndex = state.parsedCSV?.findIndex(findCSVCb) ?? -1;
						const existingStateCSV = state.parsedCSV?.[existingStateCSVIndex];
						const accumCSV = accum.get(existingStateCSVIndex);

						if (!accumCSV) {
							accum.set(existingStateCSVIndex, {
								...(existingStateCSVIndex !== -1 ? existingStateCSV : {}),
								configurations: [current]
							});
						} else {
							accum.set(existingStateCSVIndex, {
								...accumCSV,
								configurations: [...(accumCSV?.configurations ?? []), current]
							});
						}
						return accum;
					}, new Map())
					?.values() ?? []
			);

			const allExistingConfigs = recreatedCSVData?.flatMap((csv) => csv?.configurations);

			if (!state.parsedTempCSV) state.parsedTempCSV = recreatedCSVData;

			if (importedCSVData) {
				const importedCSVDataCodes = recreatedCSVData?.flatMap((e) =>
					e?.configurations?.map((e) => e?.globals?.productLine?.code)
				);
				importedCSVDataCodes?.forEach((code) => {
					// we want to find an existing config with a given product line code
					// and then apply the account and billing info to all configs with our product line
					const existingConfigInProductLine = allExistingConfigs?.find(
						(config) => config?.globals?.productLine?.code === code
					);
					// this needs to be only for configs in the product line
					const updatedConfigs = importedCSVData?.configurations?.map((config) => {
						if (config.globals?.productLine?.code === code) {
							return {
								...config,
								accountId: existingConfigInProductLine?.accountId,
								accountName: existingConfigInProductLine?.accountName,
								accountNumber: existingConfigInProductLine?.accountNumber,
								billToId: existingConfigInProductLine?.billToId
							};
						}
						return config;
					});
					importedCSVData.configurations = updatedConfigs;
				});

				state?.parsedTempCSV?.push(importedCSVData);
			}
		},
		clearParsedCSV: (state) => {
			state.parsedCSV = undefined;
		},
		clearTempParsedCSV: (state) => {
			state.parsedTempCSV = undefined;
		},
		updateGlobalAttributes: (state, action: PayloadAction<models["PendingOrderConfigurationViewModel"]>) => {
			state.updatedGlobalAttributes = { ...state.updatedGlobalAttributes, ...action.payload };
		},
		updateAccountNumber: (
			state,
			action: PayloadAction<{
				fileIndex?: number;
				productLineCode: string | undefined | null;
				newAccountNumber: models["CustomerAccountViewModel"] | null;
			}>
		) => {
			const { fileIndex, productLineCode, newAccountNumber } = action.payload;
			if (fileIndex !== undefined && productLineCode !== undefined && ~fileIndex && productLineCode) {
				(state?.parsedTempCSV ?? state?.parsedCSV)?.forEach((file) => {
					file?.configurations?.forEach((configuration) => {
						if (configuration.globals?.productLine?.code === productLineCode) {
							configuration.accountId = newAccountNumber === null ? null : newAccountNumber?.accountId;
							configuration.accountName =
								newAccountNumber === null ? null : newAccountNumber?.accountName;
							configuration.accountNumber =
								newAccountNumber === null ? null : newAccountNumber?.accountNumber;
						}
					});
				});
			}
			if (productLineCode !== undefined && state.draftDetails?.draftOrder?.configurations && productLineCode) {
				state.draftDetails?.draftOrder.configurations.forEach((configuration) => {
					if (configuration.globals?.productLine?.code === productLineCode) {
						configuration.accountId = newAccountNumber === null ? null : newAccountNumber?.accountId;
						configuration.accountName = newAccountNumber === null ? null : newAccountNumber?.accountName;
						configuration.accountNumber =
							newAccountNumber === null ? null : newAccountNumber?.accountNumber;
					}
				});
			}
		},
		updateBillToNumber: (
			state,
			action: PayloadAction<{
				fileIndex?: number;
				productLineCode: string | undefined | null;
				newBillToNumber: models["BillToViewModel"] | null | undefined;
			}>
		) => {
			const { fileIndex, productLineCode, newBillToNumber } = action.payload;
			if (fileIndex !== undefined && productLineCode !== undefined && ~fileIndex && productLineCode) {
				(state?.parsedTempCSV ?? state?.parsedCSV)?.forEach((file) => {
					file?.configurations?.forEach((configuration) => {
						if (configuration.globals?.productLine?.code === productLineCode) {
							configuration.billToId = newBillToNumber === null ? null : newBillToNumber?.billToId;
						}
					});
				});
			}
			if (productLineCode !== undefined && state.draftDetails?.draftOrder?.configurations && productLineCode) {
				state.draftDetails.draftOrder.configurations.forEach((configuration) => {
					if (configuration.globals?.productLine?.code === productLineCode) {
						configuration.billToId = newBillToNumber?.billToId ?? "";
					}
				});
			}
		},
		addValidatedOrder: (state, action) => {
			state.validatedOrderResponse = action.payload;
			if (state.draftDetails) {
				state.draftDetails = {
					...state.draftDetails,
					draftOrder: {
						...state.draftDetails?.draftOrder,
						pricing: action.payload?.pricing
					}
				};
			}
		},
		clearNewOrder: (state) => {
			state.newAddresses = [];
			state.parsedCSV = undefined;
			state.validatedOrderResponse = undefined;
			state.newGlobalAttributes = undefined;
			state.updatedGlobalAttributes = undefined;
			state.newOrderDetails = undefined;
			state.draftDetails = undefined;
			state.catalogs = undefined;
		},
		leaveNewOrderFlow: (state, action) => {
			state.leaveNewOrderFlow = action.payload;
		},
		continueNewOrderFlow: (state) => {
			if (state.leaveNewOrderFlow) {
				state.leaveNewOrderFlow = { ...state.leaveNewOrderFlow, state: "proceed" };
			}
		},
		resetNewOrderFlow: (state) => {
			state.leaveNewOrderFlow = undefined;
		},
		addProductLineAccountInfo: (
			state,
			{ payload: [productLineCode, data] }: PayloadAction<[string, GetAccountsResponseData]>
		) => {
			state.productLineAccounts = {
				...state.productLineAccounts,
				[productLineCode]: data
			};
		},
		overwriteOrderDetails: (
			state,
			action: { payload: models["PendingOrderViewModel"] & { orderProcessFee?: boolean } }
		) => {
			state.newOrderDetails = {
				...action.payload,
				tandemOrderId: state.newOrderDetails?.tandemOrderId ?? action.payload.tandemOrderId,
				orderProcessFee: action.payload.orderProcessFee ?? state.newOrderDetails?.orderProcessFee ?? false
			};
		},
		updateShippingDetails: (state, action: { payload: models["ShipToViewModel"] }) => {
			state.newOrderDetails = {
				...state.newOrderDetails,
				shipToId: convertShipToToAddress(action.payload).shipToId,
				shipToAddress: convertShipToToAddress(action.payload).address
			};
		},
		addSingleConfigToOrder: (
			state,
			action: { payload: models["PendingOrderConfigurationViewModel"] | undefined }
		) => {
			if (action.payload) {
				state.newOrderDetails = {
					...state.newOrderDetails,
					configurations: [...(state.newOrderDetails?.configurations || []), action.payload]
				};
				state.draftDetails = {
					...state.draftDetails,
					draftError: state.draftDetails?.draftError ?? undefined,
					draftOrder: {
						...state.draftDetails?.draftOrder,
						configurations: [...(state.draftDetails?.draftOrder?.configurations || []), action.payload]
					},
					currentDraftId: state?.draftDetails?.currentDraftId,
					currentTemplateId: state?.draftDetails?.currentTemplateId
				};
			}
			state.newGlobalAttributes = action.payload;
		},
		addMultiConfigsToOrder: (state, action: { payload: models["PendingOrderConfigurationViewModel"][] }) => {
			state.newOrderDetails = {
				...state.newOrderDetails,
				configurations: [...(state.newOrderDetails?.configurations || []), ...action.payload]
			};
			state.draftDetails = {
				draftError: state.draftDetails?.draftError ?? undefined,
				draftOrder: {
					...state.draftDetails?.draftOrder,
					configurations: [...(state.draftDetails?.draftOrder?.configurations || []), ...action.payload]
				},
				currentDraftId: state?.draftDetails?.currentDraftId,
				currentTemplateId: state?.draftDetails?.currentTemplateId
			};
		},
		overwriteDraftOrder: (state, action: { payload: models["DraftOrderViewModel"] }) => {
			state.draftDetails = {
				draftError: state.draftDetails?.draftError ?? undefined,
				draftOrder: {
					...action.payload,
					tandemOrderId: state.newOrderDetails?.tandemOrderId ?? action.payload.tandemOrderId
				},
				currentDraftId: state?.draftDetails?.currentDraftId,
				currentTemplateId: state?.draftDetails?.currentTemplateId
			};
		},
		updateDraftError: (state, action) => {
			state.draftDetails = {
				draftOrder: state.draftDetails?.draftOrder,
				draftError: action.payload,
				currentDraftId: state?.draftDetails?.currentDraftId,
				currentTemplateId: state?.draftDetails?.currentTemplateId
			};
		},
		clearDraftError: (state) => {
			state.draftDetails = {
				draftOrder: state.draftDetails?.draftOrder,
				draftError: undefined,
				currentDraftId: state?.draftDetails?.currentDraftId,
				currentTemplateId: state?.draftDetails?.currentTemplateId
			};
		},
		updateColorMatch: (
			state,
			action: {
				payload: {
					configurationId: string | null | undefined;
					colorMatch: models["CustomColorGlobalViewModel"];
				};
			}
		) => {
			const { configurationId, colorMatch } = action.payload;
			if (state.newOrderDetails?.configurations) {
				state.newOrderDetails.configurations = state.newOrderDetails.configurations.map((config) => {
					if (config.configurationId === configurationId) {
						return {
							...config,
							globals: {
								...config.globals,
								finish: {
									...config.globals?.finish,
									customColor: colorMatch
								}
							}
						};
					}
					return config;
				});
			}
			if (state.draftDetails?.draftOrder?.configurations) {
				state.draftDetails.draftOrder.configurations = state.draftDetails.draftOrder.configurations.map(
					(config) => {
						if (config.configurationId === configurationId) {
							return {
								...config,
								globals: {
									...config.globals,
									finish: {
										...config.globals?.finish,
										customColor: colorMatch
									}
								}
							};
						}
						return config;
					}
				);
			}
		},
		addDuplicateConfiguration: (
			state,
			action: {
				payload: {
					configuration:
						| models["PendingOrderConfigurationViewModel"]
						| models["ValidatedOrderConfigurationViewModel"];
					oldConfigId: string | null | undefined;
					newConfigId: string | undefined;
				};
			}
		) => {
			const oldConfigurationId = action.payload.oldConfigId;
			const newConfigurationId = action.payload.newConfigId;
			const configurationToDuplicate = state.newOrderDetails?.configurations?.find(
				(config) => config.configurationId === oldConfigurationId
			);
			if (state.newOrderDetails?.configurations) {
				state.newOrderDetails.configurations.push({
					...configurationToDuplicate,
					configurationId: newConfigurationId,
					lineItems: []
				});
			}
			if (state.draftDetails?.draftOrder?.configurations) {
				state.draftDetails.draftOrder?.configurations.push({
					...action.payload.configuration,
					configurationId: newConfigurationId,
					lineItems: []
				});
			}
		},
		addCustomFinishCatalogs: (
			state,
			action: {
				payload: {
					catalogsWithCustomFinish: models["CatalogViewModel"][];
					catalogsWithoutCustomFinish: models["CatalogViewModel"][];
				};
			}
		) => {
			state.catalogs = {
				catalogsWithCustomFinish: action.payload.catalogsWithCustomFinish,
				catalogsWithoutCustomFinish: action.payload.catalogsWithoutCustomFinish
			};
		}
	}
});

export const newOrderActions = newOrderSlice.actions;
export default newOrderSlice.reducer;
